import bluebird from 'bluebird';
import { begin, end, pendingTask } from 'react-redux-spinner';
import { gql } from '@apollo/client';
import { toNumber } from 'lodash-es';
import { getApolloClient } from 'api/apolloClient';
import {
  type Listing,
  type PsaGroupedByCountryCodeResult,
  type QueryCommercialPropertiesArgs,
  type Query,
  type QueryResidentialPropertiesArgs,
  type QueryVacationPropertiesArgs,
  type SearchParamsInput,
} from 'server/types/graphql';

// ------------------------------------
// Constants
// ------------------------------------
export const EMPTY_RESULT_LIST = {
  commercialHits: [] as Listing[],
  residentialHits: [] as Listing[],
  vacationHitGroups: [] as PsaGroupedByCountryCodeResult[],
  isLoading: false,
};

// ------------------------------------
// Action constants
// ------------------------------------
export const actionTypes = {
  RECEIVE_RESIDENTIAL_PROPERTIES: 'RECEIVE_RESIDENTIAL_PROPERTIES',
  RECEIVE_COMMERCIAL_PROPERTIES: 'RECEIVE_COMMERCIAL_PROPERTIES',
  RECEIVE_VACATION_PROPERTIES: 'RECEIVE_VACATION_PROPERTIES',
  START_LOADING_PROPERTIES: 'START_LOADING_PROPERTIES',
  DONE_LOADING_PROPERTIES: 'DONE_LOADING_PROPERTIES',
};

// ------------------------------------
// Actions
// ------------------------------------
export const setStartLoadingProperties = () => ({
  type: actionTypes.START_LOADING_PROPERTIES,
  meta: { [pendingTask]: begin }, // starts spinner
});

export const setDoneLoadingProperties = () => ({
  type: actionTypes.DONE_LOADING_PROPERTIES,
  meta: { [pendingTask]: end }, // ends spinner
});

export const receiveCommericalProperties = (hits: Listing[]) => ({
  type: actionTypes.RECEIVE_COMMERCIAL_PROPERTIES,
  hits,
});

export const receiveResidentialProperties = (hits: Listing[]) => ({
  type: actionTypes.RECEIVE_RESIDENTIAL_PROPERTIES,
  hits,
});

export const receiveVacationProperties = (
  groups: PsaGroupedByCountryCodeResult[],
) => ({
  type: actionTypes.RECEIVE_VACATION_PROPERTIES,
  groups,
});

const HITS_FRAGMENT = gql`
  fragment HitsFragment on Listing {
    meta {
      hasChildren
    }
    addressString
    badges {
      label
      value
    }
    exposeId
    isPrivate
    headline
    links {
      targetURL
    }
    mainKeyFacts {
      value
      label
    }
    primaryPriceKeyFact {
      value
      label
    }
    realtorLogoImageProps {
      src
    }
    primaryPictureImageProps {
      src
    }
  }
`;

const GET_COMMERCIAL_PROPERTIES = gql`
  ${HITS_FRAGMENT}

  query commercialProperties($params: SearchParamsInput) {
    commercialProperties(params: $params) {
      ...HitsFragment
    }
  }
`;

const GET_RESIDENTIAL_PROPERTIES = gql`
  ${HITS_FRAGMENT}

  query residentialProperties($params: SearchParamsInput) {
    residentialProperties(params: $params) {
      ...HitsFragment
    }
  }
`;

const GET_VACATION_PROPERTIES = gql`
  ${HITS_FRAGMENT}

  query vacationProperties($accountCwid: String, $size: Int) {
    vacationProperties(accountCwid: $accountCwid, size: $size) {
      countryCode
      data {
        hits {
          ...HitsFragment
        }
      }
    }
  }
`;

/**
 * Fetches the complete list of properties for the existing page.
 */
export const fetchProperties =
  (query: SearchParamsInput) => (dispatch: any) => {
    return bluebird
      .try(() => dispatch(setStartLoadingProperties()))
      .then(async () => {
        const { data } = await getApolloClient().query<
          Pick<Query, 'commercialProperties'>,
          QueryCommercialPropertiesArgs
        >({
          query: GET_COMMERCIAL_PROPERTIES,
          variables: { params: query },
        });
        return data.commercialProperties;
      })
      .then((result: Listing[]) =>
        dispatch(receiveCommericalProperties(result)),
      )
      .catch(() => {
        dispatch(receiveCommericalProperties(EMPTY_RESULT_LIST.commercialHits));
      })
      .then(async () => {
        const { data } = await getApolloClient().query<
          Pick<Query, 'residentialProperties'>,
          QueryResidentialPropertiesArgs
        >({
          query: GET_RESIDENTIAL_PROPERTIES,
          variables: { params: query },
        });
        return data.residentialProperties;
      })
      .then((result: Listing[]) =>
        dispatch(receiveResidentialProperties(result)),
      )
      .catch(() =>
        dispatch(
          receiveResidentialProperties(EMPTY_RESULT_LIST.residentialHits),
        ),
      )
      .then(async () => {
        const { data } = await getApolloClient().query<
          Pick<Query, 'vacationProperties'>,
          QueryVacationPropertiesArgs
        >({
          query: GET_VACATION_PROPERTIES,
          variables: {
            accountCwid: query.accountCwid,
            size: toNumber(query.size),
          },
        });

        return data.vacationProperties;
      })
      .then((result: PsaGroupedByCountryCodeResult[]) =>
        dispatch(receiveVacationProperties(result)),
      )
      .catch(() =>
        dispatch(
          receiveVacationProperties(EMPTY_RESULT_LIST.vacationHitGroups),
        ),
      )
      .then(() => dispatch(setDoneLoadingProperties()));
  };

// ------------------------------------
// Reducer
// ------------------------------------
export const reducer = (state = EMPTY_RESULT_LIST, action: any) => {
  switch (action.type) {
    case actionTypes.RECEIVE_COMMERCIAL_PROPERTIES:
      return {
        ...state,
        commercialHits: action.hits,
      };
    case actionTypes.RECEIVE_RESIDENTIAL_PROPERTIES:
      return {
        ...state,
        residentialHits: action.hits,
      };
    case actionTypes.RECEIVE_VACATION_PROPERTIES:
      return {
        ...state,
        vacationHitGroups: action.groups,
      };
    case actionTypes.START_LOADING_PROPERTIES:
      return {
        ...state,
        isLoading: true,
      };
    case actionTypes.DONE_LOADING_PROPERTIES:
      return {
        ...state,
        isLoading: false,
      };
    default:
      return state;
  }
};
